import { useCallback, useEffect, useState } from 'react';

import {
  countSavedDeals,
  listSavedDeals,
  removeSavedDeal,
  SavedDealCapError,
  type SavedDealRowJoined,
  type SavedDealsSort,
} from '@/lib/db/buyer';
import type { DealKind, DealView } from '@/lib/db/deals';

// ---------------------------------------------------------------------------
// Row → DealView mapper
// ---------------------------------------------------------------------------
// The saved_deals join selects a *subset* of deal columns (no description,
// no photos, no admin-only fields) — Spec V1.1 §M7 row card needs title,
// zone, community, price, discount, bedrooms, sqft only. Missing fields are
// filled with safe defaults so <DealCard> renders without warnings. The card
// falls back to <ImagePlaceholder> when `imageUrl` is undefined, which is
// acceptable for the Saved list (V1 — full image hydration deferred).

export function mapSavedRowToDealView(row: SavedDealRowJoined): DealView | null {
  const deal = row.deals;
  if (!deal) return null;

  const original = Number(deal.original_price ?? 0);
  const asking = Number(deal.asking_price ?? 0);
  const area = deal.area_sqft ?? 0;
  const status: DealKind = deal.type === 'off_plan' ? 'OFF-PLAN' : 'READY';

  // Cover photo — same rule as the catalogue (mapDealRowToDealView): sort by
  // position, prefer position 0, fall back to the first. <DealCard> renders
  // <ImagePlaceholder> when imageUrl is undefined (deal without photos).
  const photos = [...(deal.deal_photos ?? [])].sort((a, b) => a.position - b.position);
  const cover = photos.find((p) => p.position === 0) ?? photos[0] ?? null;

  return {
    id: deal.id,
    referenceId: deal.reference_id,
    slug: deal.slug,
    community: deal.community,
    zone: deal.zone,
    buildingTitle: deal.title,
    building: deal.building,
    bedrooms: deal.bedrooms ?? 0,
    bathrooms: 0,
    areaSqft: area,
    askingAed: asking,
    dldReferenceAed: original,
    discountPct: deal.discount_pct ?? 0,
    dldConfidence: (deal.dld_confidence as DealView['dldConfidence']) ?? null,
    dldScope: deal.dld_scope ?? null,
    pricePerSqftAed: area > 0 ? Math.round(asking / area) : 0,
    benchmarkSqftAed: area > 0 && original > 0 ? Math.round(original / area) : 0,
    status,
    // Raw type drives the off-plan "Original price" wording (vs ready "DLD ref").
    dealType: deal.type as 'ready' | 'off_plan',
    imageLabel: cover?.alt_text ?? deal.title,
    imageUrl: cover?.url,
    photos: photos.map((p) => ({
      url: p.url,
      alt_text: p.alt_text ?? deal.title,
      position: p.position,
    })),
    isFeatured: false,
    isPick: false,
    publishedAt: deal.created_at,
    description: '',
  };
}

// ---------------------------------------------------------------------------
// useSavedDeals — paginated read with sort + manual refresh after remove
// ---------------------------------------------------------------------------

export type SavedDealsState =
  | { status: 'loading'; deals: null; error: null }
  | { status: 'success'; deals: DealView[]; error: null }
  | { status: 'error'; deals: null; error: Error };

const INITIAL_STATE: SavedDealsState = { status: 'loading', deals: null, error: null };

export function useSavedDeals(sort: SavedDealsSort): {
  state: SavedDealsState;
  refresh: () => void;
} {
  const [state, setState] = useState<SavedDealsState>(INITIAL_STATE);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => {
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState(INITIAL_STATE);

    listSavedDeals(sort)
      .then((rows) => {
        if (cancelled) return;
        const mapped = rows.map(mapSavedRowToDealView).filter((d): d is DealView => d !== null);
        setState({ status: 'success', deals: mapped, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          deals: null,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [sort, tick]);

  return { state, refresh };
}

// ---------------------------------------------------------------------------
// useSavedCount — head:true count for cap warning banner (ADR-025 §C)
// ---------------------------------------------------------------------------
// Threshold 450 = "approaching cap" → bandeau warning. Hard cap 500 enforced
// by the DB trigger. The hook re-fetches whenever the consumer asks for a
// refresh (e.g. after a successful remove).

export type SavedCountState =
  | { status: 'loading'; count: 0 }
  | { status: 'success'; count: number }
  | { status: 'error'; count: 0 };

export function useSavedCount(userId: string | null): {
  state: SavedCountState;
  refresh: () => void;
} {
  const [state, setState] = useState<SavedCountState>({ status: 'loading', count: 0 });
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => {
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    if (!userId) {
      setState({ status: 'success', count: 0 });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading', count: 0 });

    countSavedDeals(userId)
      .then((count) => {
        if (cancelled) return;
        setState({ status: 'success', count });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ status: 'error', count: 0 });
      });

    return () => {
      cancelled = true;
    };
  }, [userId, tick]);

  return { state, refresh };
}

// ---------------------------------------------------------------------------
// useRemoveSavedDeal — one-shot mutation wrapper with status flag
// ---------------------------------------------------------------------------
// Exposes `remove(dealId)` returning a Promise so the page can chain a
// refresh + toast in the same flow. Status flag prevents double-click while
// the request is in flight.

export type RemoveSavedDealStatus = 'idle' | 'removing' | 'error';

export function useRemoveSavedDeal(userId: string | null): {
  status: RemoveSavedDealStatus;
  remove: (dealId: string) => Promise<boolean>;
} {
  const [status, setStatus] = useState<RemoveSavedDealStatus>('idle');

  const remove = useCallback(
    async (dealId: string): Promise<boolean> => {
      if (!userId) return false;
      setStatus('removing');
      try {
        await removeSavedDeal({ userId, dealId });
        setStatus('idle');
        return true;
      } catch (err) {
        setStatus('error');
        // Re-throw so the page can surface a localised toast; cap errors are
        // never thrown by remove (cap only triggers on INSERT).
        if (err instanceof SavedDealCapError) throw err;
        throw err instanceof Error ? err : new Error(String(err));
      }
    },
    [userId],
  );

  return { status, remove };
}

// Threshold for the "approaching cap" warning banner (ADR-025 §C).
export const SAVED_DEALS_WARNING_THRESHOLD = 450;
